import React from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
  Badge,
  Button,
  Switch,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@tonic/shared/components/ui';
import { Plus, Settings, Edit, Trash, ArrowUpRight } from 'lucide-react';

// Mock data for IKEA repair programs
const repairPrograms = [
  {
    id: 'RP001',
    name: 'IKEA Furniture Care',
    description: 'Basic repair and maintenance program for all IKEA furniture',
    status: 'active',
    eligibility: [
      'Must be IKEA product',
      'Within 365 days of purchase',
      'Original receipt required',
      'Product must be assembled correctly'
    ],
    coverage: [
      'Hardware replacement',
      'Basic repairs',
      'Assembly corrections',
      'Parts replacement'
    ],
    pricing: {
      basic: 29.99,
      standard: 49.99,
      premium: 79.99
    },
    metrics: {
      totalRepairs: 1245,
      successRate: 94,
      avgRepairTime: 2.5,
      customerSatisfaction: 4.8
    }
  },
  {
    id: 'RP002',
    name: 'IKEA Professional Assembly Repair',
    description: 'Specialized repair service for professionally assembled furniture',
    status: 'active',
    eligibility: [
      'Must be IKEA product',
      'Assembled by IKEA professional service',
      'No time limit',
      'Proof of professional assembly required'
    ],
    coverage: [
      'Complete disassembly/reassembly',
      'Structural repairs',
      'Hardware upgrades',
      'Finish touch-ups'
    ],
    pricing: {
      basic: 49.99,
      standard: 89.99,
      premium: 129.99
    },
    metrics: {
      totalRepairs: 856,
      successRate: 97,
      avgRepairTime: 1.8,
      customerSatisfaction: 4.9
    }
  },
  {
    id: 'RP003',
    name: 'IKEA Sofa & Upholstery Care',
    description: 'Specialized care and repair for sofas and upholstered furniture',
    status: 'active',
    eligibility: [
      'IKEA upholstered products only',
      'Within 730 days of purchase',
      'Original receipt required',
      'No intentional damage'
    ],
    coverage: [
      'Fabric repair',
      'Cushion replacement',
      'Frame repair',
      'Spring maintenance',
      'Upholstery cleaning'
    ],
    pricing: {
      basic: 79.99,
      standard: 149.99,
      premium: 199.99
    },
    metrics: {
      totalRepairs: 623,
      successRate: 91,
      avgRepairTime: 3.2,
      customerSatisfaction: 4.7
    }
  }
];

export function RepairProgramPage() {
  const [activeTab, setActiveTab] = React.useState('overview');

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Repair Programs</h1>
          <p className="text-sm text-muted-foreground">
            Manage and configure repair service programs
          </p>
        </div>
        <Button>
          <Plus className="w-4 h-4 mr-2" />
          New Program
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {repairPrograms.map((program) => (
          <Card key={program.id}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle>{program.name}</CardTitle>
                  <CardDescription>{program.description}</CardDescription>
                </div>
                <Badge variant={program.status === 'active' ? 'default' : 'secondary'}>
                  {program.status}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="font-medium mb-2">Pricing Tiers</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Basic</span>
                    <span className="font-medium">${program.pricing.basic}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Standard</span>
                    <span className="font-medium">${program.pricing.standard}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Premium</span>
                    <span className="font-medium">${program.pricing.premium}</span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-medium mb-2">Performance</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Success Rate</p>
                    <p className="text-lg font-medium">{program.metrics.successRate}%</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Repairs</p>
                    <p className="text-lg font-medium">{program.metrics.totalRepairs}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Avg. Time</p>
                    <p className="text-lg font-medium">{program.metrics.avgRepairTime} days</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Satisfaction</p>
                    <p className="text-lg font-medium">{program.metrics.customerSatisfaction}/5</p>
                  </div>
                </div>
              </div>

              <div className="pt-4 flex justify-end gap-2">
                <Button variant="outline" size="sm">
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                <Button variant="outline" size="sm">
                  <Settings className="w-4 h-4 mr-2" />
                  Configure
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Program Details</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="eligibility">Eligibility</TabsTrigger>
              <TabsTrigger value="coverage">Coverage</TabsTrigger>
              <TabsTrigger value="pricing">Pricing</TabsTrigger>
              <TabsTrigger value="metrics">Metrics</TabsTrigger>
            </TabsList>

            {repairPrograms.map((program) => (
              <React.Fragment key={program.id}>
                <TabsContent value="eligibility" className="mt-6">
                  <div className="space-y-4">
                    <h3 className="font-medium">Eligibility Criteria</h3>
                    <ul className="list-disc pl-6 space-y-2">
                      {program.eligibility.map((criterion, index) => (
                        <li key={index}>{criterion}</li>
                      ))}
                    </ul>
                  </div>
                </TabsContent>

                <TabsContent value="coverage" className="mt-6">
                  <div className="space-y-4">
                    <h3 className="font-medium">Coverage Details</h3>
                    <ul className="list-disc pl-6 space-y-2">
                      {program.coverage.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </TabsContent>
              </React.Fragment>
            ))}
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
